<script lang="ts" setup>
import { ConsultationSourceEnum } from '@forgd/contract/types'
import {
  closeSlideover,
  handleSlideoverChange,
  isNavTargetActive,
  NAV_RAIL_COLLAPSED_WIDTH,
  NAV_RAIL_EXPANDED_WIDTH,
  NAV_RAIL_RESPONSIVE_EXPAND_MIN_WIDTH_PX,
  NavDrawer,
  NavRail,
  NavRailItem,
  slideoverMenu,
} from '@forgd/ui-kit/blocks/nav'
import { ForgdToolsIcon } from '@forgd/ui-kit/icons'
import { Tooltip, TooltipArrow, TooltipContent, TooltipTrigger } from '@forgd/ui-kit/tooltip'
import {
  AcademicCapIcon,
  ArrowRightStartOnRectangleIcon,
  CalendarDaysIcon,
  HomeIcon,
  InformationCircleIcon,
  PresentationChartBarIcon,
  SparklesIcon,
  UsersIcon,
} from '@heroicons/vue/24/outline'
import { useMediaQuery } from '@vueuse/core'
import { useAdvisor } from '#advisor-portal/state/useAdvisor'
import { drawerNavTargets } from '#core/utils/nav-drawers'

interface HomeNavItem {
  label: string
  icon: Component
  to?: string
  external?: boolean
  exact?: boolean
  shouldHighlight?: (currentPath: string) => boolean
  slideover?: string
  onClick?: () => void
  testId?: string
}

defineOptions({
  inheritAttrs: false,
})

const expanded = useNavExpanded()
const auth = useAuth()
const advisor = useAdvisor()
const route = useRoute()
const NuxtLink = resolveComponent('NuxtLink')

watch(() => route.fullPath, () => closeSlideover())

const { destination } = useConsultationDestination()
const { captureConsultationClick } = useConsultationTracking()

const homeLink = useHomeNavLink()
const drawers = useHomeNavDrawers()

const mainItems = computed<HomeNavItem[]>(() => {
  const homeItem: HomeNavItem = { ...homeLink.value, icon: HomeIcon }
  const toolsItem: HomeNavItem = advisor.isAdvisorDefault
    ? { label: 'Manage Teammates', icon: UsersIcon, to: '/advisor-portal/teammates' }
    : { label: 'Free Tools', icon: ForgdToolsIcon, slideover: 'home-tools' }
  return [
    homeItem,
    toolsItem,
    { label: 'Research & Insights', icon: PresentationChartBarIcon, slideover: 'home-research' },
    { label: 'Forgd Academy', icon: AcademicCapIcon, to: '/academy', shouldHighlight: (currentPath: string) => currentPath.includes('/academy') },
  ]
})
const aboutItem: HomeNavItem = { label: 'About Forgd', icon: InformationCircleIcon, slideover: 'home-about' }

const isAiChatEnabled = useFeatureEnabled('forgd-ai-chat')
const aiChatItem: HomeNavItem = { label: 'Forgd AI', icon: SparklesIcon, to: '/ask', shouldHighlight: (currentPath: string) => currentPath.endsWith('/ask'), testId: 'nav-forgd-ai' }

const exitProjectItem = computed<HomeNavItem | null>(() => {
  if (auth.isAdvisor && advisor.isInProject && !advisor.isInPortal) {
    return {
      icon: ArrowRightStartOnRectangleIcon,
      label: `Exit ${advisor.project?.name ?? ''}`,
      onClick: advisor.exitProject,
      testId: 'exit-project',
    }
  }
  return null
})

const isResponsiveExpanded = useMediaQuery(`(min-width: ${NAV_RAIL_RESPONSIVE_EXPAND_MIN_WIDTH_PX}px)`)
const drawerOffset = computed(() => {
  const isExpanded = expanded.value === 'responsive' ? isResponsiveExpanded.value : expanded.value
  return isExpanded ? NAV_RAIL_EXPANDED_WIDTH : NAV_RAIL_COLLAPSED_WIDTH
})

const groupClass = computed(() => {
  if (expanded.value === 'responsive') {
    return 'items-center nav-expanded:items-stretch'
  }
  return expanded.value ? '' : 'items-center'
})

function isActive(item: HomeNavItem) {
  if (isNavTargetActive(route.path, item)) {
    return true
  }
  const drawerId = item.slideover
  if (!drawerId) {
    return false
  }
  const drawer = drawers.value[drawerId as keyof typeof drawers.value]
  if (!drawer) {
    return false
  }
  return drawerNavTargets(drawer).some(target => isNavTargetActive(route.path, target))
}

function isOpen(item: HomeNavItem) {
  return !!item.slideover && slideoverMenu.value === item.slideover
}

function itemAs(item: HomeNavItem) {
  if (!item.to) {
    return 'button'
  }
  return item.external ? 'a' : NuxtLink
}

function itemLinkProps(item: HomeNavItem) {
  const extra = { 'onClick': item.onClick, 'data-testid': item.testId, 'data-nav-slideover': item.slideover }
  if (!item.to) {
    return extra
  }
  if (item.external) {
    return { href: item.to, target: '_blank', rel: 'noopener noreferrer', ...extra }
  }
  return { to: item.to, ...extra }
}
</script>

<template>
  <NavRail class="hidden md:block" :expanded="expanded" aria-label="Home Navigation" v-bind="$attrs">
    <template #logo>
      <NuxtLink :to="homeLink.to" aria-label="Forgd home" class="[&_img]:w-30">
        <template v-if="expanded === 'responsive'">
          <span class="flex nav-expanded:hidden">
            <CoreLogo collapsed />
          </span>
          <span class="hidden nav-expanded:flex">
            <CoreLogo />
          </span>
        </template>
        <CoreLogo v-else :collapsed="!expanded" />
      </NuxtLink>
    </template>

    <div class="flex flex-col gap-1 px-4 py-2" :class="groupClass">
      <NavRailItem
        v-for="item in mainItems"
        :key="item.label"
        :as="itemAs(item)"
        :label="item.label"
        :expanded="expanded"
        :active="isActive(item)"
        :open="isOpen(item)"
        :has-slideover="!!item.slideover"
        v-bind="itemLinkProps(item)"
        @open="handleSlideoverChange({ event: $event, slideover: item.slideover ?? null })"
        @close="handleSlideoverChange({ event: $event, slideover: null })"
      >
        <template #icon>
          <component :is="item.icon" class="size-5 shrink-0" />
        </template>
      </NavRailItem>
    </div>

    <div class="flex flex-col gap-1 px-4 py-2" :class="groupClass">
      <NavRailItem
        v-if="isAiChatEnabled"
        :as="itemAs(aiChatItem)"
        :label="aiChatItem.label"
        :expanded="expanded"
        :active="isActive(aiChatItem)"
        v-bind="itemLinkProps(aiChatItem)"
      >
        <template #icon>
          <component :is="aiChatItem.icon" class="size-5 shrink-0" />
        </template>
      </NavRailItem>
      <NavRailItem
        :as="itemAs(aboutItem)"
        :label="aboutItem.label"
        :expanded="expanded"
        :active="isActive(aboutItem)"
        :open="isOpen(aboutItem)"
        :has-slideover="!!aboutItem.slideover"
        v-bind="itemLinkProps(aboutItem)"
        @open="handleSlideoverChange({ event: $event, slideover: aboutItem.slideover ?? null })"
        @close="handleSlideoverChange({ event: $event, slideover: null })"
      >
        <template #icon>
          <component :is="aboutItem.icon" class="size-5 shrink-0" />
        </template>
      </NavRailItem>
    </div>

    <div
      v-if="exitProjectItem"
      class="flex flex-col px-4 py-2"
      :class="groupClass"
    >
      <NavRailItem
        :as="itemAs(exitProjectItem)"
        :label="exitProjectItem.label"
        :expanded="expanded"
        :active="isActive(exitProjectItem)"
        :open="isOpen(exitProjectItem)"
        :has-slideover="!!exitProjectItem.slideover"
        v-bind="itemLinkProps(exitProjectItem)"
      >
        <template #icon>
          <component :is="exitProjectItem.icon" class="size-5 shrink-0" />
        </template>
      </NavRailItem>
    </div>

    <template v-if="!auth.isAdvisor" #footer>
      <div
        class="p-4"
        :class="{ 'flex justify-center nav-expanded:block': expanded === 'responsive', 'flex justify-center': expanded === false }"
      >
        <AppNavCta v-if="expanded === true" />
        <div v-else-if="expanded === 'responsive'" class="hidden nav-expanded:block">
          <AppNavCta />
        </div>
        <Tooltip v-if="expanded !== true">
          <TooltipTrigger as-child>
            <NuxtLink
              :to="destination.to"
              :external="destination.external"
              :target="destination.target"
              :rel="destination.external ? 'noopener noreferrer' : undefined"
              aria-label="Book a Free Consultation"
              class="flex size-10 shrink-0 items-center justify-center rounded-lg text-primal-inverted transition-colors hover:bg-accent"
              :class="{ 'nav-expanded:hidden': expanded === 'responsive' }"
              @click="captureConsultationClick(ConsultationSourceEnum.NavSidebar, destination.to)"
            >
              <CalendarDaysIcon class="size-6" />
            </NuxtLink>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            :side-offset="12"
            class="bg-brand-900 px-3 py-2 text-xs font-semibold text-primal-inverted"
            :class="{ 'nav-expanded:hidden': expanded === 'responsive' }"
          >
            Book a Free Consultation
            <TooltipArrow class="fill-brand-900" />
          </TooltipContent>
        </Tooltip>
      </div>
    </template>
  </NavRail>
  <ClientOnly>
    <NavDrawer :offset="drawerOffset">
      <template #default="{ contentKey }">
        <AppNavSlideover :model-value="contentKey" />
      </template>
    </NavDrawer>
  </ClientOnly>
</template>
